import {
  describePasswordProblems,
  friendlyError,
  PASSWORD_HINT,
  passwordProblems,
  useAuth,
} from '@hobbytools/platform';
import { Banner, Button, TextField } from '@hobbytools/ui';
import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useNext } from '../lib/next';
import { AuthLayout, linkClass } from './AuthLayout';

export function SignUp() {
  const { user, isLoading, signUp } = useAuth();
  const { next, go, query } = useNext();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && user) go();
  }, [isLoading, user, go]);

  const problems = passwordProblems(password);
  const passwordError = password.length > 0 ? describePasswordProblems(problems) : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { needsConfirmation } = await signUp({ email: email.trim(), password, displayName, next });
      if (needsConfirmation) setPendingEmail(email.trim());
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  if (pendingEmail) {
    return (
      <AuthLayout title="Check your email" lead="One more step before the dice come out.">
        <Banner tone="success">
          We sent a confirmation link to <strong>{pendingEmail}</strong>. Open it on this device to finish
          signing up.
        </Banner>
        <p className="text-sm text-fg-muted">
          Opened it somewhere else? Your account is still confirmed.{' '}
          <Link className={linkClass} to={`/sign-in${query}`}>
            Sign in here
          </Link>
          .
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      lead="One account for every game system."
      footer={
        <>
          Already have an account?{' '}
          <Link className={linkClass} to={`/sign-in${query}`}>
            Sign in
          </Link>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <TextField
          label="Display name"
          description="What other players see. You can change it later."
          autoComplete="nickname"
          isRequired
          maxLength={60}
          value={displayName}
          onChange={setDisplayName}
        />
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          isRequired
          value={email}
          onChange={setEmail}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          description={PASSWORD_HINT}
          errorMessage={passwordError ?? undefined}
          isRequired
          value={password}
          onChange={setPassword}
        />
        <Button type="submit" isDisabled={busy || !displayName.trim() || !email || problems.length > 0}>
          {busy ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  );
}
